import {
  type HeightSettings,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
  VISUAL_EFFECT_OPTIONS,
} from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { generateVideoPlayerFormField } from './generation/form-field';
import { videoPlayerV2Migration } from './migrations/002-video-player.migration';
import { videoPlayerV3PaddingToMarginMigration } from './migrations/003-video-player-padding-to-margin.migration';
import { VideoPlayerComponent } from './video-player.component';

export type VideoPlayerDef = WithProps<VideoPlayerPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    videoPlayer: VideoPlayerDef;
  }
}

const DEFAULT_WIDTH = 716;
const DEFAULT_HEIGHT = 410;
const MIN_WIDTH = 24;
const MIN_HEIGHT = 24;
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1920;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

const VIDEO_VISUAL_EFFECT_OPTIONS = VISUAL_EFFECT_OPTIONS.filter((o) => o.value !== 'inset-shadow');

const VIDEO_ASPECT_RATIO_OPTIONS: SelectOption<string | undefined>[] = [
  { label: 'Landscape (16 : 9)', value: '16 / 9' },
  { label: 'Landscape Short (4 : 3)', value: '4 / 3' },
  { label: 'Portrait (9 : 16)', value: '9 / 16' },
  { label: 'Portrait Short (3 : 4)', value: '3 / 4' },
  { label: 'Square (1 : 1)', value: '1' },
  { label: 'Custom', value: '' },
];

registerElementDefinition({
  type: 'videoPlayer',
  componentType: VideoPlayerComponent,
  isListed: true,
  name: 'Video Player',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'auto' },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  generation: {
    formField: generateVideoPlayerFormField,
  },
  groups: ['advanced'],
  initial: getInitial(),
  styleIncludePaths: ['style'],
  version: 3,
  migrations: {
    2: videoPlayerV2Migration,
    3: videoPlayerV3PaddingToMarginMigration,
  },
});

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type VideoPlayerPropsModel = PropsModel & {
  content: {
    videoSource: {
      enabled: boolean;
      url: string;
    };
    options: {
      autoplay: boolean;
      autoplayMute: boolean;
      loop: boolean;
      hideControls: boolean;
      aspectRatio: string;
    };
    actions: {
      play: {
        enabled: boolean;
        workflowId?: string;
      };
    };
    title: Omit<ElementLabelProps, 'align' | 'iconPosition'>;
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    video: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      visualEffect?: PropertiesConfigInputVisualEffectModel;
    };
    general: {
      title: PropertiesConfigInputColorModel;
    };
    labelBlock: ElementLabelStyleProps;
    outline: {
      color: PropertiesConfigInputColorModel;
      width: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
    };
  };
};

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'action', schema: DEFAULT_SCHEMA }],
  };
}

function getDefaults(): PropsOf<'videoPlayer'> {
  return {
    content: {
      videoSource: {
        enabled: true,
        url: '',
      },
      options: {
        autoplay: false,
        autoplayMute: true,
        loop: false,
        hideControls: false,
        aspectRatio: '16 / 9',
      },
      actions: {
        play: {
          enabled: false,
        },
      },
      title: {
        enabled: false,
        label: 'Video Player',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      video: {
        offset: {
          margin: '$t.spacingInput',
        },
      },
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        fontStyle: [],
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
        position: 'left',
        align: 'left',
        width: '30%',
        iconPosition: 'left',
      },
      outline: {
        color: { color1: '$t.outline', selectorType: 'none' },
        width: '1px',
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'videoPlayer'> {
  return {
    style: {
      labelBlock: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('videoPlayer', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.videoSource',
        'Choose Video Source',
        [
          epc.fields.uploadField(
            'content.videoSource.url',
            'Type URL to video file or upload the file',
            {
              dataType: 'video',
              placeholderExtension: 'mp4',
              note: 'Note: links to video platforms are not supported',
            },
          ),
        ],
        {
          optional: true,
          lockWhen: (model: unknown) =>
            getValueByPath(model, 'data.?key:value.dataSource')
              ? 'The Value of the element is bound'
              : undefined,
          dataBindings: [
            {
              key: 'value',
              label: 'URL',
              sources: 'field',
              bindings: [{ property: 'value', label: 'URL', dataType: 'String' }],
            },
          ],
        },
      ),
      epc.subsections.subsection('content.options', 'Options', [
        epc.fields.toggleField('content.options.autoplay', 'Autoplay'),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.options.autoplayMute', 'Mute', {
            renderType: 'check',
            indent: true,
            tooltip:
              'Required for autoplay unless triggered by user action. Browsers block autoplay with sound unless the playback is initiated by user interaction.',
          }),
          'content.options.autoplay',
          (autoplay: boolean) => !autoplay,
        ),
        epc.fields.toggleField('content.options.loop', 'Loop'),
        epc.fields.toggleField('content.options.hideControls', 'Hide video controls'),
        epc.utils.separator(),
        epc.utils.observedField(
          epc.fields.selectFieldFromList(
            'content.options.aspectRatio',
            'Ratio',
            VIDEO_ASPECT_RATIO_OPTIONS,
            { showNoneOption: false, showSelection: true },
          ),
          (_model: unknown, aspectRatio: string) => {
            const height: HeightSettings = aspectRatio
              ? { mode: 'auto' }
              : { mode: 'value', value: DEFAULT_HEIGHT, fixed: true };
            return { 'root.height': height };
          },
        ),
      ]),
      epc.subsections.subsection('content.actions', 'Actions', [
        epc.fields.toggleField('content.actions.play.enabled', 'When video plays', {
          workflowId: (model: PropsOf<'videoPlayer'>) => model.content.actions.play.workflowId,
        }),
      ]),
      epc.subsections.subsection('content.size', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_WIDTH, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_HEIGHT, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
          (
            model: unknown,
            options: SelectOption<ElementSizingMode>[],
          ): SelectOption<ElementSizingMode>[] => {
            const aspectRatio = (model as VideoPlayerPropsModel).content.options.aspectRatio;
            return options.filter(({ value }) => value === (aspectRatio ? 'auto' : 'fixed'));
          },
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.video', 'Margin', [
          epc.fields.spacingAndAlignField('style.video.offset', {
            hasMargin: true,
          }),
        ]),
        epc.subsections.subsection('style.outline', 'Outline', [
          epc.fields.colorField('style.outline.color', 'Color', {
            allowGradient: false,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.outline.width', 'Width', [{ min: 0, max: 10, unit: 'px' }]),
          epc.fields.radiusField('style.outline.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.video.visualEffect', VIDEO_VISUAL_EFFECT_OPTIONS),
        ]),
        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'style.labelBlock.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'style.labelBlock.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'style.labelBlock.position',
            width: 'style.labelBlock.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}
